import { useEffect, useRef, useState } from 'react'

import { useServerTimeOffsetMs } from '~/hooks/useServerTimeOffsetMs'
import type { CivilDate } from '~/models/civilDate'
import { now as serverNow } from '~/models/clock'
import type { GateCloseFrontier } from '~/models/gateClose'
import { chartGcLineKoma } from '~/pages/DemandForecastDaily/models/gcCountdown'

/** GC カウントダウン (`components/GcCountdown.tsx`) と同じ更新間隔。 */
const UPDATE_INTERVAL_MS = 1_000

type GcLine = { frontier: GateCloseFrontier; targetDate: CivilDate }

const lineOf = (gcLine: GcLine): number | undefined =>
  chartGcLineKoma(gcLine.frontier, gcLine.targetDate, serverNow())

/**
 * チャートの GC 縦線のコマ (AC-DFD-006a-5)。`gcLine` が無い (当日断面以外・page
 * 取得前) 時は undefined。
 *
 * 画面を開いたまま GC を跨いでも線が動くよう、カウントダウンと同じ間隔で page の
 * GC 境界を現在時刻まで進め直す。値はコマ番号なので 30 分に 1 度しか変わらず、
 * 変わらない間は React が再描画を省く。
 */
export function useChartGcLineKoma(
  gcLine: GcLine | undefined,
): number | undefined {
  const [koma, setKoma] = useState(() =>
    gcLine === undefined ? undefined : lineOf(gcLine),
  )
  const offsetMs = useServerTimeOffsetMs()
  // view は render ごとに組み直されて gcLine の参照が毎回変わるため、タイマーは
  // gcLine の有無でだけ張り直し、tick では最新の gcLine を読む。
  const gcLineRef = useRef(gcLine)
  gcLineRef.current = gcLine
  const hasGcLine = gcLine !== undefined

  useEffect(() => {
    if (!hasGcLine) return
    const update = (): void => {
      const current = gcLineRef.current
      if (current !== undefined) setKoma(lineOf(current))
    }
    update()
    const timer = setInterval(update, UPDATE_INTERVAL_MS)
    return () => {
      clearInterval(timer)
    }
  }, [hasGcLine, offsetMs])

  return hasGcLine ? koma : undefined
}
